import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

interface CreateMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierName?: string;
  supplierId?: string;
  editingMaterial?: any;
  onSuccess?: (material: any) => void;
}

export function CreateMaterialDialog({
  open,
  onOpenChange,
  supplierName,
  supplierId,
  editingMaterial,
  onSuccess,
}: CreateMaterialDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    unit: "UND",
    cost_per_unit: "",
    category: "",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      if (editingMaterial) {
        setFormData({
          code: editingMaterial.code || "",
          name: editingMaterial.name || "",
          unit: editingMaterial.unit || "UND",
          cost_per_unit: editingMaterial.cost_per_unit?.toString() || "",
          category: editingMaterial.category || "",
          notes: editingMaterial.notes || "",
        });
      } else {
        setFormData({
          code: "",
          name: "",
          unit: "UND",
          cost_per_unit: "",
          category: "",
          notes: "",
        });
      }
    }
  }, [open, editingMaterial]);

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      toast.error("El código es obligatorio");
      return;
    }
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        unit: formData.unit,
        cost_per_unit: formData.cost_per_unit ? parseFloat(formData.cost_per_unit) : null,
        category: formData.category.trim() || 'Composite',
        notes: formData.notes.trim() || null,
        supplier: supplierName || null,
        is_active: true,
      };

      // If we have a supplier ID from billing_customers, add it
      if (supplierId) {
        payload.billing_customer_id = supplierId;
      }

      let result;
      if (editingMaterial?.id) {
        const { data, error } = await api
          .from("raw_materials")
          .update(payload)
          .eq("id", editingMaterial.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
        toast.success("Material actualizado");
      } else {
        const { data, error } = await api
          .from("raw_materials")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        result = data;
        toast.success("Material creado");
      }

      onSuccess?.(result);
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving material:", error);
      toast.error(error.message || "Error al guardar material");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingMaterial ? "Editar Material" : "Crear Material"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {supplierName && (
            <div className="text-sm text-muted-foreground bg-muted p-2 rounded">
              Proveedor: <strong>{supplierName}</strong>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Código</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Ej: MAT-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Unidad</Label>
              <Select
                value={formData.unit}
                onValueChange={(v) => setFormData({ ...formData, unit: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UND">UND</SelectItem>
                  <SelectItem value="KG">KG</SelectItem>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="M2">M²</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="ROLLO">ROLLO</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre del material"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Precio estimado</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.cost_per_unit}
                onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ej: Consumibles"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notas adicionales"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editingMaterial ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
